import { SettingApi } from "lib/api";
import { ApiHelper, Http } from "lib/api/base";

import { PermissibleUser, Permission, Section, User, UserGroup } from "lib/data/model";
import { Right } from "lib/data/model/enumerations";
import { PermissionService } from "lib/services";

export class PermissionApi {

	public static getUserPermissions = async (userId?: number): Promise<Permission[]> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/User/${(userId !== undefined ? userId : "-")}/Permission`;

			// Make Request
			const response: Response = await Http.httpGet(url, undefined);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const permissions: Permission[] = await ApiHelper.parseResponseBody(response);

				return permissions;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return [];
	}

	public static isUserPermitted = async (user: User, permission: Permission): Promise<boolean | undefined> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Check Permission
			if (!PermissionService.isPermissionValid(permission)) {

				// Return Nothing, Cannot Search For ID-based Permissions Without The Type
				return undefined;
			}

			// Build Remainder Of Url
			const url: string = `${baseUrl}/User/${user.id}/Permitted${ApiHelper.buildQueryString(permission)}`;

			// Make Request
			const response: Response = await Http.httpGet(url, undefined);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const isPermitted: boolean = await ApiHelper.parseResponseBody(response);

				return isPermitted;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return undefined;
	}

	public static assignUserPermission = async (user: User, permission: Permission): Promise<boolean> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/User/${user.id}/Permission`;

			// Check For Permission
			if (!PermissionService.isPermissionValid(permission)) {

				// Return Nothing, Cannot Search For ID-based Permissions Without The Type
				return false;
			}

			// Make Request
			const response: Response = await Http.httpPost(url, permission);

			// Check Response
			if (response.ok) {

				// Return Success
				return true;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return false;
	}

	public static assignUserPermissions = async (user: User, permissions: Permission[]): Promise<boolean> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/User/${user.id}/Permission/Bulk`;

			// Check Permission
			permissions = permissions.filter((permission) => {

				// Check Permission Object
				return PermissionService.isPermissionValid(permission);
			});

			// Check For Permissions
			if (permissions.length > 0) {

				// Make Request
				const response: Response = await Http.httpPost(url, permissions);

				// Check Response
				if (response.ok) {

					// Return Success
					return true;
				}
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return false;
	}

	public static replaceUserPermissions = async (user: User, permissions: Permission[]): Promise<boolean> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/User/${user.id}/Permission/Replace`;

			// Check Permission
			permissions = permissions.filter((permission) => {

				// Check Permission Object
				return PermissionService.isPermissionValid(permission);
			});

			// Make Request
			const response: Response = await Http.httpPost(url, permissions);

			// Check Response
			if (response.ok) {

				// Return Success
				return true;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return false;
	}

	public static getUserGroupPermissions = async (group: UserGroup): Promise<Permission[]> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/UserGroup/${group.id}/Permission`;

			// Make Request
			const response: Response = await Http.httpGet(url, undefined);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const permissions: Permission[] = await ApiHelper.parseResponseBody(response);

				return permissions;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return [];
	}

	public static isUserGroupPermitted = async (group: UserGroup, permission: Permission): Promise<boolean | undefined> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Check Permission
			if (!PermissionService.isPermissionValid(permission)) {

				// Return Nothing, Cannot Search For ID-based Permissions Without The Type
				return undefined;
			}

			// Build Remainder Of Url
			const url: string = `${baseUrl}/UserGroup/${group.id}/Permitted${ApiHelper.buildQueryString(permission)}`;

			// Make Request
			const response: Response = await Http.httpGet(url, undefined);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const isPermitted: boolean = await ApiHelper.parseResponseBody(response);

				return isPermitted;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return undefined;
	}

	public static assignUserGroupPermission = async (group: UserGroup, permission: Permission): Promise<boolean> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/User/${group.id}/Permission`;

			// Check Permission
			if (!PermissionService.isPermissionValid(permission)) {

				// Return Nothing, Cannot Search For ID-based Permissions Without The Type
				return false;
			}

			// Make Request
			const response: Response = await Http.httpPost(url, permission);

			// Check Response
			if (response.ok) {

				// Return Success
				return true;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return false;
	}

	public static assignUserGroupPermissions = async (group: UserGroup, permissions: Permission[]): Promise<boolean> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/User/${group.id}/Permission/Bulk`;

			// Filter Permissions
			permissions = permissions.filter((permission) => {

				// Check Permission
				return PermissionService.isPermissionValid(permission);
			});

			// Check For Permissions
			if (permissions.length > 0) {

				// Make Request
				const response: Response = await Http.httpPost(url, permissions);

				// Check Response
				if (response.ok) {

					// Return Success
					return true;
				}
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return false;
	}
	public static getPermittedSectionUsers = async (right: Right, section: Section): Promise<PermissibleUser[]> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/User/Permissible?right=${right}&recordType=Section&recordId=${section.id}`;

			// Make Request
			const response: Response = await Http.httpGet(url, undefined);

			// Check Response
			if (response.ok) {

				const users: PermissibleUser[] = await ApiHelper.parseResponseBody(response);

				// Return Success
				return users;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return [];
	}

	public static deleteUserPermission = async (user: User, permission: Permission): Promise<boolean> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/User/${user.id}/Permission`;


			// Make Request
			const response: Response = await Http.httpDelete(url, permission);

			// Check Response
			if (response.ok) {

				// Return Success
				return true;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return false;
	}

	public static deleteUserGroupPermission = async (group: UserGroup, permission: Permission): Promise<boolean> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/UserGroup/${group.id}/Permission`;


			// Make Request
			const response: Response = await Http.httpDelete(url, permission);

			// Check Response
			if (response.ok) {

				// Return Success
				return true;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return false;
	}
}
